import { useEffect, useRef, useState } from "react";

/**
 * Hook to manage animation values for budget adjustments.
 * Shows temporary adjustment amounts when budget options are added.
 *
 * @param {Object} params
 * @param {Object} params.budgetPlan - The current budget plan
 * @param {boolean} params.isAddBudgetPlanOptionSuccess - Whether an option was just added
 * @returns {Object} Animation state values
 */
export const useAdjustmentAnimations = ({
  budgetPlan,
  isAddBudgetPlanOptionSuccess,
}) => {
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [savingsAdjustmentAmount, setSavingsAdjustmentAmount] = useState(0);
  const previousBudgetPlan = useRef();
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Skip if no previous plan to compare against
    if (!previousBudgetPlan.current) {
      previousBudgetPlan.current = budgetPlan;
      return;
    }

    // This check avoids displaying the animation when re-opening the budgeting
    // game. Only show animation when an option was just added or living
    // situation changed.
    const shouldAnimate =
      isAddBudgetPlanOptionSuccess ||
      previousBudgetPlan.current?.isLivingAlone !== budgetPlan?.isLivingAlone;

    if (!shouldAnimate) {
      previousBudgetPlan.current = budgetPlan;
      return;
    }

    const previousCosts = previousBudgetPlan.current?.budgetTotals?.costs ?? 0;
    const currentCosts = budgetPlan?.budgetTotals?.costs ?? 0;
    const previousSavings =
      previousBudgetPlan.current?.budgetTotals?.savings ?? 0;
    const currentSavings = budgetPlan?.budgetTotals?.savings ?? 0;

    const costChange = previousCosts - currentCosts;
    const savingsChange = currentSavings - previousSavings;

    // Skip if nothing actually changed — this happens when the budget plan
    // query refetches with the same data (new object reference, same values),
    // or when isAddBudgetPlanOptionSuccess flips to true before the refetch
    // delivers updated data. Without this guard the animation timeout gets
    // replaced and the amounts immediately reset to 0.
    if (costChange === 0 && savingsChange === 0) {
      previousBudgetPlan.current = budgetPlan;
      return;
    }

    // Clear any existing animation timeout before starting a new one
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (savingsChange === 0) {
      // We won't show the adjustment amount on the salary display if the
      // savings changed.
      setAdjustmentAmount(costChange);
    }
    setSavingsAdjustmentAmount(savingsChange);
    previousBudgetPlan.current = budgetPlan;

    timeoutRef.current = setTimeout(() => {
      setAdjustmentAmount(0);
      setSavingsAdjustmentAmount(0);
      timeoutRef.current = undefined;
    }, 1500);
  }, [budgetPlan, isAddBudgetPlanOptionSuccess]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    adjustmentAmount,
    savingsAdjustmentAmount,
  };
};
